import {
    DisplayValue,
    Workspace,
    ThemeIcons,
    User,
    isFeatureAvailableForUser,
    PolicyAcknowledgment,
    Organization,
} from '@newgenus/common';
import { distinctUntilChanged, take, takeUntil, filter, Observable, Subject, Subscription } from 'rxjs';
import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { selectWorkspace } from '../../redux/workspace/workspace.selectors';
import { trigger, style, transition, animate } from '@angular/animations';
import { SecurityService } from '../../shared/services/security.service';
import { NetworkService } from '../../shared/services/network.service';
import { ProfilePhotoService } from '../../shared/services/profile-photo.service';
import { ThemeService } from '../../shared/services/theme.service';
import { NotificationService } from '../../shared/services/notification.service';
import { IssueTrackerAccessService } from '../../issues/issue-tracker-access.service';
import { AngularFireStorage } from '@newgenus/fire-compat/storage';
import { AuthService } from '../../shared/services/auth.service';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../shared/services/api.service';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { StoreUser } from '../../workspace/user.abstract';
import { VERSION } from '../../../environments/version';
import { HttpClient } from '@angular/common/http';
import { extractSignatureImageUrls } from './signature-urls';
import { displayBasketCount } from '../../workspace/basket-count';
import { AppState } from '../../redux/reducers';
import { select, Store } from '@ngrx/store';
import firebase from 'firebase/compat/app';
import { NavigationEnd, Router } from '@angular/router';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { MatDialog } from '@angular/material/dialog';
import {
    PolicyAcknowledgmentDialogComponent,
    PolicyAcknowledgmentDialogData,
} from '@newgenus/angular-shared-components';
import {
    ProfilePhotoUploadDialogComponent,
    ProfilePhotoUploadDialogResult,
} from '../../shared/modals/profile-photo-upload-dialog/profile-photo-upload-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppUpdateService } from '../../shared/services/app-update.service';

@Component({
    selector: 'newgenus-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    animations: [
        trigger('valueAnimation', [
            transition(':increment', [
                style({ color: '#4a4a4aa3', 'background-color': '#8a8a8aa3' }),
                animate('0.4s ease-out', style('*')),
            ]),
            transition(':decrement', [
                style({ color: '#4a4a4aa3', 'background-color': '#8a8a8aa3' }),
                animate('0.4s ease-out', style('*')),
            ]),
        ]),
    ],
    standalone: false,
})
export class HeaderComponent extends StoreUser implements OnInit {
    @Input() hats!: any;
    @Input() settings!: any;
    // eslint-disable-next-line @angular-eslint/no-output-native -- long-standing name; renaming touches every host template (OE-213 follow-up)
    @Output() toggle = new EventEmitter<void>();

    public name!: string;
    public popValue!: string;
    public profileUrl!: string;
    public firstName?: string;
    public lastName?: string;
    public userInitials = '';
    public photoUrl!: string;
    public photoSrc: string | ArrayBuffer = '';
    public profileColor = '';
    public date = 'N/A';
    public version = VERSION;
    public popsettings = false;
    public pophats = false;
    // public clientLinkedEmails: { gmail: string, outlook: string } = { gmail: undefined, outlook: undefined };
    public themeIcon!: ThemeIcons;
    public selectedOrg?: string;
    public organizationList: Array<DisplayValue> = [];

    public hasWorkspaceAccess = false;
    public isLinked = false;
    public isSubNavSidebarClosed = false;
    public inbasketCount = 0;
    public pendingCount = 0;

    public isFocusMode = false;
    public isSmallDisplay = false;
    /** True while on a /app/workspace route — gates the mobile header search icon
     *  (we only search the workspace for now, not the whole app). */
    public isWorkspaceRoute = false;

    // Policy notifications
    public pendingPoliciesCount = 0;
    public pendingPolicies: PolicyAcknowledgment[] = [];
    private policySubscription?: Subscription;
    private currentOrganization?: Organization;

    private destroy$ = new Subject<void>();
    public basketCount$: any;
    public environment = '';
    public emulator = false;
    private workspace$!: Observable<Workspace[]>;
    private workspaceCountsSub?: Subscription;
    public invisibleSignatures = new Set<string>();

    constructor(
        private networkService: NetworkService,
        private storage: AngularFireStorage,
        private fbAuth: AngularFireAuth,
        private themeService: ThemeService,
        private apiService: ApiService,
        private httpClient: HttpClient,
        public override store: Store<AppState>,
        public auth: AuthService,
        public security: SecurityService,
        private router: Router,
        private db: AngularFirestore,
        private dialog: MatDialog,
        private profilePhotoService: ProfilePhotoService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
        public notificationService: NotificationService,
        public appUpdateService: AppUpdateService,
        private issueTrackerAccess: IssueTrackerAccessService
    ) {
        super(store);
        this.initStoreUser(this.destroy$);
    }

    public ngOnInit(): void {
        this.themeIcon = this.themeService.getThemeIcon();

        // Show confirmation toast if this is the first load after a forced update
        if (this.appUpdateService.justUpdated) {
            setTimeout(() => {
                this.snackBar.open(`✅ Successfully updated to ${this.version.hash} 🚀`, 'OK', {
                    duration: 8000,
                    panelClass: ['update-success-snackbar'],
                });
            }, 1500);
        }

        this.user$.pipe(takeUntil(this.destroy$)).subscribe((users) => {
            if (users && users[0]) {
                if (this.user.firstName) {
                    this.firstName = this.user.firstName;
                    this.lastName = this.user.lastName;
                    this.userInitials = this.nameAcronym(this.firstName, this.lastName);
                }
                this.isLinked = this.isGmailLinked || this.isOutlookLinked;

                // Load profile placeholder color from user data
                this.profileColor = (this.user as any).profileColor || '';

                this.loadProfileImageOnce(this.user);
                this.cacheUsersSignatures();
                this.loadUserOrganizations();
                this.hasWorkspaceAccess = isFeatureAvailableForUser(this.user, 'workspace');

                // Subscribe directly to Firestore workspace doc for inbasket/pending counts
                if (this.hasWorkspaceAccess && this.user.uid && !this.workspaceCountsSub) {
                    this.workspaceCountsSub = this.db
                        .doc<any>(`workspace/${this.user.uid}`)
                        .valueChanges()
                        .pipe(takeUntil(this.destroy$))
                        .subscribe((doc) => {
                            if (doc) {
                                this.inbasketCount = displayBasketCount(doc.inbasketCount);
                                this.pendingCount = displayBasketCount(doc.pendingCount);
                            }
                        });
                }

                // Initialize notification service for this user
                if (this.user.uid) {
                    this.notificationService.initialize(this.user.uid);
                    // Eager central session (ISSUE-TRACKER-V2-DESIGN.md §3): only exchanges a
                    // central token / merges the bell when the issue tracker is actually on for
                    // this user — a no-op for everyone else, and a no-op after the first grant.
                    this.issueTrackerAccess
                        .bootstrapIfAllowed(this.user, this.notificationService)
                        .catch((ex) => console.error('header: issue tracker access bootstrap failed', ex));
                }
            }
        });

        this.themeService.localSidebarStatus$
            .asObservable()
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((sidebarStatus) => {
                this.isSubNavSidebarClosed = sidebarStatus;
            });

        this.themeService.isSmallDisplay$
            .asObservable()
            .pipe(takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe((isSmallDisplay) => {
                setTimeout(() => {
                    this.isSmallDisplay = isSmallDisplay;
                });
            });

        this.themeService.isFocusModeEnabled$
            .asObservable()
            .pipe(takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe((isFocusModeEnabled) => {
                this.isFocusMode = isFocusModeEnabled;
            });

        this.workspace$ = this.store.pipe(select(selectWorkspace));
        this.workspace$.subscribe((workspace) => {
            if (workspace.length > 0) {
                this.inbasketCount = displayBasketCount(workspace[0].inbasketCount);
                this.pendingCount = displayBasketCount(workspace[0].pendingCount);
            }
        });

        // Track whether we're on a workspace route so the mobile search icon only
        // shows there (search is workspace-scoped for now).
        const setWsRoute = (url: string): void => {
            this.isWorkspaceRoute = (url || '').startsWith('/app/workspace');
        };
        setWsRoute(this.router.url);
        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe((e) => setWsRoute(e.urlAfterRedirects));

        this.environment = environment.env;
        this.emulator = environment.useEmulators;
    }

    private cacheUsersSignatures() {
        // Extraction lives in signature-urls.ts with its own tests: the version
        // that used to live here matched to the next WHITESPACE and then chopped
        // one character off every match to shed a closing quote — which amputated
        // a real character whenever the URL wasn't quote-terminated (OE-159).
        // These are preloaded as invisible images, warming the cache for compose.
        for (const url of extractSignatureImageUrls(this.getEmailSignatures())) {
            this.invisibleSignatures.add(url);
        }
    }

    private getEmailSignatures(): string[] {
        const sigPref = this.user.preferences?.signature || {};
        const sigType = sigPref?.type || '';

        if (sigType === 'OneForAll' && sigPref.defaultContent) {
            return [sigPref.defaultContent];
        } else if (sigType === 'EachToTheirOwn' || sigType === 'LiquoriceAllSorts') {
            const results = [];

            Object.entries(this.user.linkedEmails || {}).forEach((x) => {
                Object.entries(x[1]).forEach((y) => {
                    // Add signature if it exists.
                    if (y[1].signature !== undefined) {
                        results.push(y[1].signature);
                    }
                });
            });

            // Add default signature.
            if (sigType === 'LiquoriceAllSorts' && sigPref.defaultContent) {
                results.push(sigPref.defaultContent);
            }

            // Return matching signature
            return results;
        }

        // No signature setup.
        return [];
    }

    public emitToggle(): void {
        this.toggle.emit();
    }

    async linkGmailAccount() {
        (window as any).location = environment.gcp.oauthLink;
    }

    async unlinkGmailAccount() {
        try {
            console.log('TODO: what account are you disconnecting here?');
            // // const email = await firebase.auth().currentUser.email;
            // const idToken = await firebase.auth().currentUser.getIdToken();
            // return await this.apiService.unlinkFromGmail(idToken, this.clientLinkedEmails.gmail)
            //   .toPromise().then((email) => {
            //     return email;
            //   }).catch(err => {
            //     console.error('Error occurred', err); // TODO check if no sensitive data is printed out
            //   });
        } catch (error) {
            console.error('Error occurred', error); // TODO check if no sensitive data is printed out
        }
    }

    public async linkMicrosoftAccount(): Promise<void> {
        const idToken = await firebase.auth().currentUser?.getIdToken();
        (window as any).location =
            `${environment.emulator_config.useAuthEmulator ? environment.emulator_config.azureOauthLink : environment.azure.oauthLink}?idToken=${idToken}&origin=${encodeURIComponent(window.location.origin)}`;
    }

    public async unlinkMicrosoftAccount(): Promise<void> {
        console.log('TODO: what account are you disconnecting here?');
        // const idToken = await firebase.auth().currentUser.getIdToken();
        // await this.apiService.unlinkMicrosoftAccount(idToken, this.clientLinkedEmails.outlook).toPromise()
        //   .then((email) => email)
        //   .catch(err => console.error('Error occurred', err)); // TODO check if no sensitive data is printed out
    }

    public toggleTheme(event: { stopPropagation: () => void }): void {
        event.stopPropagation();
        this.themeIcon = this.themeService.toggleTheme();
    }

    /** Display name of the currently-selected org (for the switcher button label). */
    public get selectedOrgDisplay(): string | undefined {
        return this.organizationList.find((o) => o.value === this.selectedOrg)?.display;
    }

    /** Pick an org from the switcher menu, then run the existing change handler. */
    public selectOrg(value: string): void {
        this.selectedOrg = value;
        this.onChangeOrgs();
    }

    public onChangeOrgs(): void {
        // Sentinel value from the "+ Create new organization" mat-option (P1).
        if (this.selectedOrg === '__create_new_org__') {
            // Restore the previously-selected org so the dropdown doesn't show the
            // sentinel after we navigate away.
            this.selectedOrg = this.user?.organizations?.selected;
            this.router.navigate(['/app/organization/new']);
            return;
        }
        if (
            this.selectedOrg !== undefined &&
            this.user?.organizations &&
            this.user.organizations.selected !== this.selectedOrg
        ) {
            this.user.organizations.selected = this.selectedOrg;
            this.updateUserStoreDoc();
            // Subscribe to pending policies for the newly selected organization
            this.updateCurrentOrganizationAndSubscribe(this.selectedOrg);
        }
    }

    private updateCurrentOrganizationAndSubscribe(orgKey: string): void {
        if (this.user?.organizations && this.user.organizations[orgKey]) {
            this.currentOrganization = {
                key: orgKey,
                name: this.user.organizations[orgKey].name,
                // Add other required Organization fields as needed
            } as Organization;
            this.subscribeToPendingPolicies(orgKey);
        }
    }

    private loadUserOrganizations(): void {
        this.organizationList = [];
        if (this.user.organizations != undefined) {
            this.selectedOrg = this.user.organizations.selected;
            for (const [key, value] of Object.entries(this.user.organizations)) {
                if (key != 'orgIds' && key != 'selected')
                    this.organizationList.push({ display: value.name, value: key });
            }

            // Defensive: the currently selected org may have been deleted (e.g. wizard org
            // cleaned up server-side, or user removed from membership). The user doc updates
            // with the org map entry gone, but `selected` may still point at the now-missing
            // key, leaving the header dropdown blank and the org board rendering stale cached
            // data until refresh. Mirror the auth-guard fallback (auth-guard.service.ts ~L175):
            // pick the first valid sibling org, or send the user to /app/home if none.
            const orgsMap = this.user.organizations as any;
            const isSelectedValid = this.selectedOrg && orgsMap[this.selectedOrg];
            if (!isSelectedValid) {
                const candidateIds: string[] = Array.isArray(orgsMap.orgIds) ? orgsMap.orgIds : [];
                const fallback = candidateIds.find((id) => id && orgsMap[id]);
                if (fallback) {
                    console.warn(
                        `Header > selected org <${this.selectedOrg}> is no longer valid; falling back to <${fallback}>.`
                    );
                    this.selectedOrg = fallback;
                    orgsMap.selected = fallback;
                    this.updateUserStoreDoc({ organizations: orgsMap });
                    this.router.navigate(['/app/organization/dashboard']);
                } else {
                    // No valid orgs — a new signup or last-org-deleted user. Don't yank
                    // them off the create-org wizard / home (where they onboard); only
                    // redirect from other routes.
                    this.selectedOrg = undefined;
                    const url = this.router.url || '';
                    const onOnboardingRoute = url.startsWith('/app/organization/new') || url.startsWith('/app/home');
                    if (!onOnboardingRoute) {
                        console.warn(`Header > user has no valid orgs left; redirecting to /app/home.`);
                        this.router.navigate(['/app/home']);
                    }
                    return;
                }
            }

            // Subscribe to pending policies for the currently selected organization
            if (this.selectedOrg) {
                this.updateCurrentOrganizationAndSubscribe(this.selectedOrg);
            }
        }
    }

    private _lastPhotoUrl: string | null = null;
    private _photoLoading = false;

    /**
     * Wrapper that prevents duplicate/concurrent profile image fetches.
     * Only calls loadProfileImage when the photoUrl actually changes.
     */
    private loadProfileImageOnce(user: User) {
        const candidateUrl = user.photoThumbnail || user.profileImage?.fileName || user.preferences?.photoURL || null;
        if (this._photoLoading || (candidateUrl && candidateUrl === this._lastPhotoUrl)) {
            return;
        }
        this._lastPhotoUrl = candidateUrl;
        this.loadProfileImage(user);
    }

    // TODO - move to service
    private loadProfileImage(user: User) {
        // console.log('loadProfileImage > user:', user);
        // this.fbAuth.currentUser.then((authUser) => {
        //   console.log('loadProfileImage > fbAuth.currentUser > user:', user);
        //   console.log('loadProfileImage > fbAuth.currentUser > authUser:', authUser);
        //   console.log('loadProfileImage > fbAuth.currentUser > authUser.photoURL:', authUser.photoURL);
        // });

        // Check for new photoThumbnail field first (from profile photo upload feature)
        if (user.photoThumbnail) {
            this.photoSrc = user.photoThumbnail;
            this.photoUrl = user.photoURL || user.photoThumbnail;
            this.updateLocalStoragePhoto(user.photoThumbnail, this.photoUrl);
            return;
        }

        if (user.profileImage !== undefined) {
            const ref = this.storage.ref(
                `user/${user.uid}/profile/` + (user.profileImage.fileName ? user.profileImage.fileName : '')
            );
            ref.getDownloadURL().subscribe((url: any) => {
                this.photoUrl = url;
                // This might not be needed anymore since the service worker might be handling this all gucci...
                this.updatePhotoSrc().then().catch();
                // this.photoSrc = url;
            });
        } else if (user.preferences.photoURL !== undefined) {
            // If the user has a profileColor set, prefer CSS initials with that color
            // over the auth-provider default avatar (e.g. Google's grey silhouette).
            if ((user as any).profileColor) {
                this.photoSrc = '';
                this.updateLocalStoragePhoto('');
                return;
            }
            this.photoUrl = user.preferences.photoURL;
            // This might not be needed anymore since the service worker might be handling this all gucci...
            this.updatePhotoSrc().then().catch();
            // this.photoSrc = user.preferences.photoURL;
        }
    }

    private async updatePhotoSrc(): Promise<void> {
        // TODO implement caching in NGRX.
        if (!this.user?.uid) return;

        let preferences: any = localStorage.getItem('userPreferences');
        if (preferences) {
            preferences = JSON.parse(preferences);
        }

        if (!preferences || preferences.uid !== this.user.uid) {
            preferences = {
                uid: this.user.uid,
                photoUrl: null,
                photoSrc: null,
            };
        }

        // If we already have a cached photoSrc for this user, use it immediately.
        // This prevents 429 (Too Many Requests) from Google profile photo URLs
        // whose query params change per session, making URL comparison always miss.
        if (preferences.photoSrc) {
            this.photoSrc = preferences.photoSrc;
            return;
        }

        if (!preferences.photoSrc || preferences.photoUrl !== this.photoUrl) {
            await this.createPhotoSrc();
            preferences.photoSrc = this.photoSrc;
            preferences.photoUrl = this.photoUrl;
        }

        this.photoSrc = preferences.photoSrc;
        localStorage.setItem('userPreferences', JSON.stringify(preferences));
    }

    private createPhotoSrc(): Promise<void> {
        this._photoLoading = true;
        return this.fetchImage(this.photoUrl)
            .then((data) => {
                this.createImageFromBlob(data);
            })
            .catch((ex) => {
                // Silently handle 429 (Too Many Requests) - cached photo will be used
                if (ex?.status !== 429) {
                    console.error('createPhotoSrc > error:', ex);
                }
            })
            .finally(() => {
                this._photoLoading = false;
            });
    }

    private fetchImage(imageUrl: string): Promise<Blob> {
        return this.httpClient.get(imageUrl, { responseType: 'blob' }).toPromise() as Promise<Blob>;
    }

    private createImageFromBlob(image: Blob) {
        const reader = new FileReader();
        reader.addEventListener(
            'load',
            () => {
                this.photoSrc = reader.result as string | ArrayBuffer;
            },
            false
        );

        if (image) {
            reader.readAsDataURL(image);
        }
    }

    /**
     * Update localStorage with new photo data
     */
    private updateLocalStoragePhoto(photoSrc: string, photoUrl?: string): void {
        if (!this.user?.uid) return;
        const preferences = {
            uid: this.user.uid,
            photoUrl: photoUrl || this.photoUrl,
            photoSrc: photoSrc,
        };
        localStorage.setItem('userPreferences', JSON.stringify(preferences));
    }

    // creates the acronym for the user profile when image is missing
    // using the first letter of first name and last name when available
    // and making sure there are no spaces used
    private nameAcronym(firstName?: string, lastName?: string): string {
        const regex = /\s/g;
        const first =
            firstName !== undefined && firstName !== undefined
                ? firstName.toUpperCase().replace(regex, '').substring(0, 1)
                : '';
        const last =
            lastName !== undefined && lastName !== undefined
                ? lastName.toUpperCase().replace(regex, '').substring(0, 1)
                : '';

        if (first && last) return first + last;
        else if (first) return first;
        else if (last) return last;
        else return '';
    }

    public linkGmail() {
        (window as any).location = environment.gcp.oauthLink;
    }

    public signOut() {
        // If the user is offline, by signing out they will lose their un-synced data.
        this.networkService.networkStatus$.pipe(take(1)).subscribe((isOnline) => {
            // If the user is online, we can sign them out immediately.
            if (isOnline) {
                this.router.navigate(['logout']);
            }
            // If the user is offline, we need to show a dialog to confirm they want to sign out.
            else if (
                confirm(
                    'You are currently offline, by signing out you will lose all your un-synced data. Are you sure you want to sign out?'
                )
            ) {
                this.router.navigate(['logout']);
            }
        });
    }

    public goToUserSettings(): void {
        const url = 'app/settings/account';
        this.router.navigate([url]);
    }

    /**
     * Open the profile photo upload dialog
     */
    public openProfilePhotoDialog(): void {
        const dialogRef = this.dialog.open(ProfilePhotoUploadDialogComponent, {
            data: {
                currentPhotoUrl: this.photoSrc || undefined,
                userId: this.user?.uid,
                currentProfileColor: this.profileColor || undefined,
                userInitials: this.userInitials || '?',
            },
            width: '600px',
            maxHeight: '90vh',
            disableClose: false,
        });

        dialogRef.afterClosed().subscribe((result: ProfilePhotoUploadDialogResult) => {
            if (!result || result.action === 'cancel') {
                return;
            }

            if (result.action === 'upload' && result.croppedImageBlob) {
                this.uploadProfilePhoto(result.croppedImageBlob);
            } else if (result.action === 'remove') {
                this.removeProfilePhoto();
            } else if (result.action === 'color' && result.profileColor) {
                this.saveProfileColor(result.profileColor);
            }
        });
    }

    /**
     * Upload the cropped profile photo
     */
    private uploadProfilePhoto(imageBlob: Blob): void {
        this.snackBar.open('Uploading profile photo...', undefined, { duration: 2000 });

        this.profilePhotoService.uploadProfilePhoto(imageBlob).subscribe({
            next: (response) => {
                console.log('Profile photo upload response:', response);
                if (response.success) {
                    this.snackBar.open('Profile photo updated!', 'Close', { duration: 3000 });
                    // Update the avatar immediately with the thumbnail
                    if (response.photoThumbnail) {
                        console.log('Setting photoSrc to thumbnail, length:', response.photoThumbnail.length);
                        this.photoSrc = response.photoThumbnail;
                        // Update localStorage cache
                        this.updateLocalStoragePhoto(response.photoThumbnail, response.photoURL);
                    } else if (response.photoURL) {
                        console.log('Setting photoSrc to URL:', response.photoURL);
                        this.photoSrc = response.photoURL;
                        // Update localStorage cache
                        this.updateLocalStoragePhoto(response.photoURL, response.photoURL);
                    }
                    // Force change detection to update the UI immediately
                    this.cdr.detectChanges();
                } else {
                    this.snackBar.open('Failed to upload photo: ' + response.message, 'Close', { duration: 5000 });
                }
            },
            error: (error) => {
                console.error('Error uploading profile photo:', error);
                this.snackBar.open('Failed to upload photo', 'Close', { duration: 5000 });
            },
        });
    }

    /**
     * Remove the user's profile photo
     */
    private removeProfilePhoto(): void {
        this.snackBar.open('Removing profile photo...', undefined, { duration: 2000 });

        this.profilePhotoService.removeProfilePhoto().subscribe({
            next: (response) => {
                if (response.success) {
                    this.snackBar.open('Profile photo removed!', 'Close', { duration: 3000 });
                    this.photoSrc = '';
                    this.photoUrl = '';
                    // Clear localStorage cache
                    this.updateLocalStoragePhoto('', '');
                    // Force change detection to update the UI immediately
                    this.cdr.detectChanges();
                } else {
                    this.snackBar.open('Failed to remove photo: ' + response.message, 'Close', { duration: 5000 });
                }
            },
            error: (error) => {
                console.error('Error removing profile photo:', error);
                this.snackBar.open('Failed to remove photo', 'Close', { duration: 5000 });
            },
        });
    }

    /**
     * Save the user's profile placeholder color
     */
    private saveProfileColor(color: string): void {
        if (!this.user?.uid) return;

        this.db
            .doc(`users/${this.user.uid}`)
            .update({ profileColor: color })
            .then(() => {
                this.profileColor = color;
                this.snackBar.open('Profile color updated!', 'Close', { duration: 3000 });
                this.cdr.detectChanges();
            })
            .catch((error) => {
                console.error('Error saving profile color:', error);
                this.snackBar.open('Failed to save profile color', 'Close', { duration: 5000 });
            });
    }

    /**
     * Subscribe to pending policy acknowledgments for the current user
     */
    private subscribeToPendingPolicies(orgKey: string): void {
        // Unsubscribe from previous subscription
        if (this.policySubscription) {
            this.policySubscription.unsubscribe();
        }

        if (!this.user?.uid || !orgKey) {
            this.pendingPolicies = [];
            this.pendingPoliciesCount = 0;
            return;
        }

        this.policySubscription = this.db
            .collection('organizations')
            .doc(orgKey)
            .collection('policyAcknowledgments', (ref) =>
                ref.where('uid', '==', this.user.uid).where('isComplete', '==', false)
            )
            .valueChanges()
            .subscribe({
                next: (acknowledgments) => {
                    this.pendingPolicies = acknowledgments as PolicyAcknowledgment[];
                    this.pendingPoliciesCount = this.pendingPolicies.length;
                },
                error: (error) => {
                    console.error('Error subscribing to pending policies:', error);
                },
            });
    }

    /**
     * Handle click on a policy notification
     */
    public onPolicyNotificationClick(policy: PolicyAcknowledgment): void {
        const dialogData: PolicyAcknowledgmentDialogData = {
            organization: this.currentOrganization!,
            acknowledgment: policy,
        };

        this.dialog.open(PolicyAcknowledgmentDialogComponent, {
            data: dialogData,
            width: '700px',
            maxHeight: '90vh',
            disableClose: false,
        });
    }

    /**
     * Navigate to the My Policies page
     */
    public openMyPoliciesDialog(): void {
        this.router.navigate(['/app/organization/my-policies']);
    }

    public ngOnDestroy(): void {
        if (this.policySubscription) {
            this.policySubscription.unsubscribe();
        }
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }
}
